"use client";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useCertificationStore } from "../../store/useCertificationStore";
import { useCandidateProfileStore } from "../../store/useCandidateProfileStore";
import DatePicker from "react-datepicker";

export type Certificate = {
  title: string;
  organization: string;
  issueDate: string;
  certDate: string;
  expDate: string;
  current: boolean;
};

type CertificateField = keyof Certificate;

type Props = {
  onClose: () => void;
  setCertificate: (data: Certificate[]) => void;
  defaultData: Certificate[];
};

export default function EditCertificateForm({
  onClose,
  setCertificate,
  defaultData,
}: Props) {

  const [certificates, setCertificates] = useState<Certificate[]>(defaultData);
  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";

  const toDate = (value?: string) =>
    value ? new Date(value) : null;

  const toString = (date: Date | null) =>
    date ? date.toISOString().slice(0, 10) : "";

  const handleChange = (i: number, name: CertificateField, value: any) => {
    setCertificates((prev) => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [name]: value };
      if (name === "current" && value === true) {
        updated[i].expDate = "";
      }


      return updated;
    });
  };

  const addCertificate = () => {
    setCertificates((prev) => [
      ...prev,
      {
        title: "",
        organization: "",
        issueDate: "",
        certDate: "",
        expDate: "",
        current: false,
      },
    ]);
  };

  const removeCertificate = (index: number) => {
    setCertificates((prev) => prev.filter((_, i) => i !== index));
  };
  const { updateCertifications, loading } = useCertificationStore();
  const { fetchCandidateProfile } = useCandidateProfileStore();

  const handleSave = async () => {
    const success = await updateCertifications(certificates);

    if (success) {
      fetchCandidateProfile();
      toast.success(lang === "id" ? "Sertifikat berhasil disimpan" : "Certificates updated successfully");
      setCertificate(certificates);
      onClose();
    } else {
      toast.error(lang === "id" ? "Gagal menyimpan sertifikat" : "Failed to update certificates");
    }
  };

  return (
    <div className="space-y-10 pb-4">
      {certificates.map((cert, index) => (
        <div key={index} className="border rounded-xl p-6 space-y-6 relative">
          {certificates.length > 1 && (
            <button
              onClick={() => removeCertificate(index)}
              className="absolute top-4 right-4 text-red-500 text-sm font-semibold"
            >
              ✕ {lang === "id" ? "Hapus" : "Remove"}
            </button>
          )}

          <h3 className="text-lg font-semibold">{lang === "id" ? "Sertifikat" : "Certificate"} {index + 1}</h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Judul Sertifikat" : "Certificate Title"}</label>
              <input
                className="w-full border rounded-lg p-3"
                value={cert.title}
                onChange={(e) => handleChange(index, "title", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Organisasi Penerbit" : "Issuer Organization"}</label>
              <input
                className="w-full border rounded-lg p-3"
                value={cert.organization}
                onChange={(e) => handleChange(index, "organization", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium">{lang === "id" ? "Tanggal Penerbitan" : "Issue Date"}</label>

                <DatePicker
                  selected={toDate(cert.issueDate)}
                  onChange={(date: any) =>
                    handleChange(index, "issueDate", toString(date))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="YYYY-MM-DD"
                  className="w-full border rounded-lg p-3"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium">{lang === "id" ? "Tanggal Berakhir" : "End Date"}</label>

                <DatePicker
                  selected={toDate(cert.expDate)}
                  onChange={(date: any) =>
                    handleChange(index, "expDate", toString(date))
                  }
                  disabled={cert.current}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="YYYY-MM-DD"
                  className="w-full border rounded-lg p-3"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>

              <label className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={cert.current}
                  onChange={(e) => handleChange(index, "current", e.target.checked)}
                />
                {lang === "id" ? "Seumur Hidup" : "Is Lifetime"}
              </label>
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-center">
        <button
          onClick={addCertificate}
          className="flex items-center gap-2 text-primary font-semibold"
        >
          ➕ {lang === "id" ? "Tambah Sertifikat" : "Add Certificate"}
        </button>
      </div>

      <div className="flex justify-center gap-4 pt-4 pb-2">
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg border text-gray-500 font-semibold"
        >
          {lang === "id" ? "Batal" : "Cancel"}
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-3 rounded-lg bg-primary text-white font-semibold"
        >
          {lang === "id" ? "Simpan" : "Save"}
        </button>
      </div>
    </div>
  );
}
